import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Row, Col } from 'react-bootstrap';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { dateAsNumbersWithoutSeconds } from '../../../utils/dateFormatter';
import { useToastContext } from '../../../toasts/toast-context';
import api from '../../../services/api';
import TrendRecordPanel from './TrendRecordPanel';

function TrendChart({ title, data, yAxisLabel, color, onPointClick }) {
  const { t } = useTranslation();

  const chartOptions = {
    chart: {
      type: 'line',
      height: 300
    },
    time: {
      useUTC: false
    },
    title: {
      text: title
    },
    xAxis: {
      type: 'datetime',
      title: { text: t('trends.date') },
      dateTimeLabelFormats: {
        day: '%e %b',
        month: '%b %Y'
      }
    },
    yAxis: {
      title: { text: yAxisLabel },
      allowDecimals: true
    },
    tooltip: {
      formatter: function () {
        return `${dateAsNumbersWithoutSeconds(this.x)}<br/><b>${parseFloat(this.y.toFixed(3))}</b>`;
      }
    },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      series: {
        cursor: onPointClick ? 'pointer' : 'default',
        point: {
          events: onPointClick
            ? {
              click: function () {
                onPointClick(this.options.custom?.recordUid);
              }
            }
            : {}
        }
      }
    },
    series: [{
      name: title,
      data: data,
      color: color,
      marker: { enabled: true, radius: 4 },
      lineWidth: 2
    }]
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
}

function computeNeighbors(data, selectedRecordUid) {
  if (!selectedRecordUid || !data) return null;
  const clickedIndex = data.findIndex(d => d.recordUid === selectedRecordUid);
  if (clickedIndex === -1) return null;
  const start = Math.max(0, clickedIndex - 5);
  const end = Math.min(data.length, clickedIndex + 6);
  const neighbors = data.slice(start, end);
  return {
    records: neighbors,
    highlightedRecordUid: selectedRecordUid,
    sliceStart: start,
    totalCount: data.length
  };
}

export default function TrendCharts(props) {
  const { data, userUid, setTrendData } = props;
  const { t } = useTranslation();
  const { showErrorToastMessage } = useToastContext();
  const [panelState, setPanelState] = useState({ recordUid: null, chartType: null });

  const handlePointClick = useCallback((chartType) => (recordUid) => {
    if (!recordUid) return;
    setPanelState({ recordUid, chartType });
  }, []);

  const handlePanelClose = useCallback(() => {
    setPanelState({ recordUid: null, chartType: null });
  }, []);

  const handleExclude = useCallback(async (recordUid) => {
    try {
      await api.delete(`/user/${userUid}/records/${recordUid}/include-in-trends`);
      setTrendData(prev => prev.filter(d => d.recordUid !== recordUid));
      if (recordUid === panelState.recordUid) {
        setPanelState({ recordUid: null, chartType: null });
      }
    } catch {
      showErrorToastMessage(t('trends.panel.excludeError'));
    }
  }, [userUid, setTrendData, showErrorToastMessage, t, panelState.recordUid]);

  const neighborInfo = computeNeighbors(data, panelState.recordUid);

  if (!data || data.length === 0) {
    return <Alert variant="info">{t('trends.noData')}</Alert>;
  }

  const mapToPoints = (items, valueAccessor) =>
    items
      .filter(d => valueAccessor(d) != null)
      .map(d => ({
        x: new Date(d.recordDate).getTime(),
        y: valueAccessor(d),
        custom: { recordUid: d.recordUid }
      }));

  const minPulseData = mapToPoints(data, d => d.minPulse);
  const maxPulseData = mapToPoints(data, d => d.maxPulse);
  const avgHRData = mapToPoints(data, d => d.averageHR);
  const stressIndexData = mapToPoints(data, d => d.stressIndex);

  const cycleParams = [
    { key: 'pMv',       titleKey: 'trends.pMv',       unitKey: 'trends.mv', color: '#3F51B5', accessor: d => d.pMv },
    { key: 'qMv',       titleKey: 'trends.qMv',       unitKey: 'trends.mv', color: '#03A9F4', accessor: d => d.qMv },
    { key: 'rMv',       titleKey: 'trends.rMv',       unitKey: 'trends.mv', color: '#9C27B0', accessor: d => d.rMv },
    { key: 'sMv',       titleKey: 'trends.sMv',       unitKey: 'trends.mv', color: '#009688', accessor: d => d.sMv },
    { key: 'tMv',       titleKey: 'trends.tMv',       unitKey: 'trends.mv', color: '#795548', accessor: d => d.tMv },
    { key: 'stMv',      titleKey: 'trends.stMv',      unitKey: 'trends.mv', color: '#E91E63', accessor: d => d.stMv },
    { key: 'dtPercent', titleKey: 'trends.dtPercent', unitKey: 'trends.pct', color: '#673AB7', accessor: d => d.dtPercent },
    { key: 'pMs',       titleKey: 'trends.pMs',       unitKey: 'trends.ms', color: '#3F51B5', accessor: d => d.pMs },
    { key: 'pqMs',      titleKey: 'trends.pqMs',      unitKey: 'trends.ms', color: '#03A9F4', accessor: d => d.pqMs },
    { key: 'prMs',      titleKey: 'trends.prMs',      unitKey: 'trends.ms', color: '#009688', accessor: d => d.prMs },
    { key: 'dprMs',     titleKey: 'trends.dprMs',     unitKey: 'trends.ms', color: '#673AB7', accessor: d => d.dprMs },
    { key: 'qrsMs',     titleKey: 'trends.qrsMs',     unitKey: 'trends.ms', color: '#795548', accessor: d => d.qrsMs },
    { key: 'dqrsMs',    titleKey: 'trends.dqrsMs',    unitKey: 'trends.ms', color: '#E91E63', accessor: d => d.dqrsMs },
    { key: 'qtMs',      titleKey: 'trends.qtMs',      unitKey: 'trends.ms', color: '#9C27B0', accessor: d => d.qtMs },
  ];

  const onMinPulseClick = userUid ? handlePointClick('minPulse') : undefined;
  const onMaxPulseClick = userUid ? handlePointClick('maxPulse') : undefined;
  const onAvgHRClick = userUid ? handlePointClick('averageHR') : undefined;
  const onStressIndexClick = userUid ? handlePointClick('stressIndex') : undefined;

  return (
    <>
      <Row className="mb-4">
        <Col md={6}>
          <TrendChart
            title={t('trends.minPulse')}
            data={minPulseData}
            yAxisLabel={t('trends.bpm')}
            color="#2196F3"
            onPointClick={onMinPulseClick}
          />
        </Col>
        <Col md={6}>
          <TrendChart
            title={t('trends.maxPulse')}
            data={maxPulseData}
            yAxisLabel={t('trends.bpm')}
            color="#F44336"
            onPointClick={onMaxPulseClick}
          />
        </Col>
      </Row>
      <Row className="mb-4">
        <Col md={6}>
          <TrendChart
            title={t('trends.averageHR')}
            data={avgHRData}
            yAxisLabel={t('trends.bpm')}
            color="#4CAF50"
            onPointClick={onAvgHRClick}
          />
        </Col>
        <Col md={6}>
          <TrendChart
            title={t('trends.stressIndex')}
            data={stressIndexData}
            yAxisLabel=""
            color="#FF9800"
            onPointClick={onStressIndexClick}
          />
        </Col>
      </Row>
      {cycleParams.reduce((rows, param, idx) => {
        if (idx % 2 === 0) rows.push([]);
        rows[rows.length - 1].push(param);
        return rows;
      }, []).map((row, rowIdx) => (
        <Row key={`cycle-row-${rowIdx}`} className="mb-4">
          {row.map(param => (
            <Col md={6} key={param.key}>
              <TrendChart
                title={t(param.titleKey)}
                data={mapToPoints(data, param.accessor)}
                yAxisLabel={t(param.unitKey)}
                color={param.color}
                onPointClick={userUid ? handlePointClick(param.key) : undefined}
              />
            </Col>
          ))}
        </Row>
      ))}
      {userUid && (
        <TrendRecordPanel
          show={!!panelState.recordUid}
          records={neighborInfo?.records}
          highlightedRecordUid={neighborInfo?.highlightedRecordUid}
          chartType={panelState.chartType}
          userUid={userUid}
          onExclude={handleExclude}
          onClose={handlePanelClose}
          totalCount={neighborInfo?.totalCount ?? 0}
          sliceStart={neighborInfo?.sliceStart ?? 0}
        />
      )}
    </>
  );
}
